import { useState, useEffect } from 'react';
import { Box, Flex, Input, Button, Avatar } from '@chakra-ui/react';
import { useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import TipTapText from '@tiptap/extension-text';
import Link from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import OrderedList from '@tiptap/extension-ordered-list';
import BulletList from '@tiptap/extension-bullet-list';
import HardBreak from '@tiptap/extension-hard-break';
import { TextDirection } from '@/ui/editor/extensions/text-direction';
import CharacterCount from '@tiptap/extension-character-count';
import { AIBlock } from './AIBlock/ai-block';
import Editor from '@/components/editor/Editor';
import { sanitizeDom } from '@/lib/purify';
import { useSaveStepsMutation } from '@/store/query/mco-step';
import { HardBreakOnEnter } from '@/ui/editor/extensions/hard-break-on-enter';
import { AttachmentPreview } from '@/components/outreach-attachment/AttachmentPreview';
import { RemoveAIBlockOnPaste } from '@/ui/editor/extensions/ai-block';
import { SizedImage } from '@/components/editor/image-extension';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';

function EditorContainer({
  step,
  aiMessages,
  scheduleDelay,
  flow,
  contactId,
  moveNextStep,
  isScheduleEdited,
  index,
  isExpanded: isAccExpanded,
}) {
  const [mailData, setMailData] = useState(step?.template?.body);
  const [subject, setSubject] = useState(step?.template?.subject);
  const [aiData, setAiData] = useState({});
  const [editedAiMessage, setEditedAIMessage] = useState('');
  const [isTemplateEdited, setIsTemplateEdited] = useState(false);
  const [isExpanded, setExpandFlag] = useState(false);
  const [saveSteps] = useSaveStepsMutation();
  const toast = useShowToast();
  const isLinkedInConnection = step?.channel === 'li_connection';

  useEffect(() => {
    if (step?.channel === 'email') {
      setMailData(step?.template?.body);
    } else {
      setMailData(sanitizeDom(step?.template?.body));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.template?.body]);

  const handleAIChange = (value) => {
    setEditedAIMessage(value);
  };

  const getCurrentMessage = (aiMessage) => {
    setAiData(aiMessage);
  };

  const handleSave = async (e) => {
    const payload = {
      id: step?.id,
      ...(isScheduleEdited && {
        step_candidate: {
          id: step?.step_candidate?.id,
          custom_schedule: scheduleDelay?.value,
        },
      }),
      template: {
        id: step?.template?.id,
        ...(step?.new_thread &&
          step?.channel !== 'li_connection' && { subject: subject }),
        body: mailData,
        edited: isTemplateEdited,
      },
      ...((aiData?.status === 'success' || aiData?.status === 'failed') &&
        aiData?.id && {
          ai_messages: {
            [aiData.id]: {
              current: true,
              edited_text:
                editedAiMessage ||
                aiData?.edited_text ||
                aiData?.generated_text,
            },
          },
        }),
    };

    if (step?.channel !== 'email') {
      payload.template.body = payload?.template?.body
        ?.replace(/<p>/g, '')
        .replace(/<\/p>/g, '')
        .replace(/<br>/g, '\n')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
    }
    payload.template.body = payload?.template?.body?.replace(
      '<ai-personalized-block></ai-personalized-block>',
      '{{candidate.personalisation}}'
    );

    const res = await saveSteps({ flowId: flow?.id, contactId, payload });
    setIsTemplateEdited(false);
    if (res?.error) {
      toast({
        description: 'Unable to update the step.',
        position: 'bottom-left',
        status: 'error',
      });
      return;
    } else {
      moveNextStep(e);
      toast({
        description: 'Step created successfully.',
        position: 'bottom-left',
        status: 'success',
      });
    }
  };

  const emailExtension = [
    StarterKit,
    Underline,
    OrderedList,
    BulletList,
    TextDirection,
    Link.configure({
      linkOnPaste: false,
      openOnClick: false,
      autolink: true,
    }),
  ];
  const linkedInExtension = [Document, Paragraph, TipTapText];
  const editor = useEditor({
    extensions: [
      ...(step?.channel === 'email' ? emailExtension : linkedInExtension),
      HardBreak,
      HardBreakOnEnter,
      AIBlock.configure({
        // Pass your custom props here
        aiMessages: aiMessages,
        channel: step?.channel,
        handleAIChange: handleAIChange,
        getCurrentMessage: getCurrentMessage,
      }),
      RemoveAIBlockOnPaste,
      SizedImage.configure({ inline: true }),
      ...(step?.channel !== 'email'
        ? [
            CharacterCount.configure({
              limit:
                step?.channel === 'li_inmail'
                  ? step?.personalisation
                    ? 1400
                    : 1900
                  : 300,
            }),
          ]
        : []),
    ],
    onUpdate: ({ editor }) => {
      setIsTemplateEdited(true);
      setMailData(editor.getHTML());
    },
    content: sanitizeDom(mailData),
  });

  const isEditorEmpty = !editor?.state?.doc?.textContent?.length;

  useEffect(() => {
    let mailBody = step?.template?.body;
    if (step?.channel !== 'email') {
      mailBody = sanitizeDom(step?.template?.body);
    }
    if (editor) {
      editor.commands.setContent(mailBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const handleSubjectChange = (e) => {
    setIsTemplateEdited(true);
    setSubject(e.target.value);
  };

  const handleReset = () => {
    let mailData = step?.template?.body;
    if (step?.channel !== 'email') {
      mailData = sanitizeDom(step?.template?.body);
    }
    setSubject(step?.template?.subject);
    setMailData(mailData);
    if (editor) editor.commands.setContent(mailData);
  };

  const renderFrom = () => (
    <Flex
      borderBottom="1px solid"
      borderColor="border-color"
      padding="10px"
      alignItems="center"
    >
      <KText as="label" size="sm-bold" display="flex">
        From:
      </KText>
      <Flex
        alignItems="center"
        gap="5px"
        px="10px"
        opacity="0.9"
        style={{
          WebkitFilter: 'grayscale(1)',
        }}
        cursor="not-allowed"
      >
        <Avatar
          className="avatar-sm"
          name={step?.sender?.name}
          width="25px"
          height="25px"
          borderRadius="8px"
          src={step?.sender?.avatar}
        />
        <KText color="secondary-700" opacity="0.5">
          {step?.sender?.name}
        </KText>
      </Flex>
    </Flex>
  );

  const renderSubject = () => (
    <Flex borderBottom="1px solid" borderColor="border-color" padding="10px">
      <KText as="label" size="sm-bold" display="flex">
        Subject:
        <Box as="pre" color="red" pl="2px">
          *
        </Box>
      </KText>
      <Flex flex="1">
        <Input
          maxlength="200"
          value={subject}
          onChange={handleSubjectChange}
          height="20px"
          fontSize="14px"
          border="none"
          _focus={{ outline: 'none' }}
          _hover={{ outline: 'none' }}
        />
        <KText
          size="sm-medium"
          color="secondary-500"
          flex={1}
          whiteSpace="nowrap"
        >
          {subject.length} / {200}
        </KText>
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" gap="8px">
      <Box
        borderRadius="8px"
        border="1px solid"
        borderColor="border-color"
        style={{ border: '1px solid #E5E5E5 !important' }}
      >
        {renderFrom()}
        <Flex flexDirection="column" onClick={() => setExpandFlag(true)}>
          {step?.new_thread &&
            step?.channel !== 'li_connection' &&
            renderSubject()}
          {isAccExpanded && (
            <Editor
              enableToolbar={step?.channel === 'email'}
              editor={editor}
              height={index === 0 && !isExpanded ? '100px' : '200px'}
              characterLimit={
                step?.channel !== 'email'
                  ? step?.channel === 'li_inmail'
                    ? step?.personalisation
                      ? 1400
                      : 1900
                    : 300
                  : null
              }
            />
          )}
          <Flex px="10px" pb="10px" gap="5px">
            {step?.template?.attachments?.map((file, idx) => (
              <AttachmentPreview key={idx} file={file} />
            ))}
          </Flex>
        </Flex>
      </Box>
      {(isExpanded || index !== 0) && (
        <Flex justifyContent="space-between">
          <Button variant="secondary" onClick={handleReset}>
            Undo
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isDisabled={
              !step?.new_thread
                ? isEditorEmpty
                : isEditorEmpty || (!isLinkedInConnection && !subject.length)
            }
          >
            Save and Continue
          </Button>
        </Flex>
      )}
    </Flex>
  );
}

export default EditorContainer;
